/**
 * The projection (D156): from `(bundle, state)` to the one view a shell
 * renders.
 *
 * @remarks
 * Before D156 `session-view.tsx` computed eight derived views itself, each
 * a `useMemo` over the bundle and the runtime state. They are the same eight
 * calls here, made once, with their asset paths already joined to the
 * game's asset base — and made in ONE place, so the browser and
 * `services/play-api` produce the identical view from the identical state.
 * That equality is the whole basis of `play: 'server'`: the shell renders
 * this, and only this, so a client that gets it over the wire holds nothing
 * a client that computed it locally would not have shown anyway.
 *
 * The output is validated against the contract at the end. That parse is
 * not paranoia: it is the proof, on every turn, that nothing from the world
 * leaked into the view through a wider engine type.
 *
 * @packageDocumentation
 */

import type { AudioId, RecordLayer, RuntimeState } from '@orpheus/content-schema';

import { type SessionView, sessionViewSchema, type TurnOutcome } from '@orpheus/api-contracts';
import { type LoadedGameBundle, resolvePackAssetUrl } from '@orpheus/content-loader';
import {
	deriveJournal,
	deriveMapGraph,
	deriveVoiceIndex,
	genesisStateOf,
	type MapGraphFlavor,
	mapGraphFlavorOf,
	type PlayerInputOutcome,
	requireCue,
	resolvePacing,
	resolveRoomAmbience,
	resolveRoomImage,
	resolveRoomSprites,
	resolveSpeakers,
	terminalEndingOf,
	transcriptEntriesOf,
} from '@orpheus/engine-runtime';

import { buildSuggestions } from './build-suggestions.js';

/** The room's picture with every path resolved, or nothing when the room shows none. */
const sceneOf = (bundle: LoadedGameBundle, state: RuntimeState): SessionView['scene'] => {
	const plate = resolveRoomImage(bundle, state);

	if (plate === undefined) {
		return undefined;
	}

	return {
		plate: {
			alt: plate.alt,
			...(plate.reducedMotionSrc === undefined ? {} : { reducedMotionSrc: resolvePackAssetUrl(bundle.runtimeManifest, plate.reducedMotionSrc) }),
			src: resolvePackAssetUrl(bundle.runtimeManifest, plate.src),
		},
		// `compositor2d` is placement's only kind today, so no filter stands
		// here — when a second renderer's kind arrives, the discriminant makes
		// this map a compile error and the filter returns WITH the union.
		sprites: resolveRoomSprites(bundle, state).map((sprite) => ({
			compositing: sprite.compositing,
			placement: { width: sprite.placement.width, x: sprite.placement.x, y: sprite.placement.y },
			...(sprite.reducedMotionSrc === undefined ? {} : { reducedMotionSrc: resolvePackAssetUrl(bundle.runtimeManifest, sprite.reducedMotionSrc) }),
			spriteId: sprite.spriteId,
			src: resolvePackAssetUrl(bundle.runtimeManifest, sprite.src),
			...(sprite.transition === undefined
				? {}
				: { transition: { fromSpriteId: sprite.transition.fromSpriteId, src: resolvePackAssetUrl(bundle.runtimeManifest, sprite.transition.src) }}),
		})),
	};
};

/** The bed to play, by URL, or quiet — or nothing when the pack gives the room no sound at all. */
const ambienceOf = (bundle: LoadedGameBundle, state: RuntimeState): SessionView['ambience'] => {
	const ambience = resolveRoomAmbience(bundle, state);

	if (ambience === undefined || ambience === 'silence') {
		return ambience;
	}

	return { key: ambience.audioId, url: resolvePackAssetUrl(bundle.runtimeManifest, ambience.src) };
};

/**
 * The map as the host DESCRIBES it (D209): the graph in each flavor the
 * pack's offered styles need, the styles, the opening one, the name and the
 * tint. The host lays nothing out — the shell does, in the style the player
 * chose, with the same engine functions — so a player's choice of map costs
 * no round trip, and a pack that offers one style sends one graph. The
 * hologram (and every levelled style) reads the graph derived with floors,
 * so `up` and `down` place and draw; the plan reads the flat one, so they
 * annotate as they always did.
 */
const mapOf = (bundle: LoadedGameBundle, state: RuntimeState): SessionView['map'] => {
	const { map } = bundle.runtimeManifest.presentation;
	const flavors = new Set<MapGraphFlavor>(map.styles.map(mapGraphFlavorOf));

	return {
		graphs: {
			...(flavors.has('flat') ? { flat: deriveMapGraph(bundle, state) } : {}),
			...(flavors.has('levelled') ? { levelled: deriveMapGraph(bundle, state, { isLevelled: true }) } : {}),
		},
		style: map.style,
		styles: map.styles,
		...(map.tint === undefined ? {} : { tint: map.tint }),
		title: map.title,
		// A fact about the session rather than about a flavor — the same set
		// both graphs count — read once from the state so no flavor is special.
		visitedCount: new Set(state.progression.visitedRoomIds).size,
	};
};

/** The parked question's face and chips, or nothing when none is parked. */
const pendingQuestionOf = (bundle: LoadedGameBundle, state: RuntimeState): SessionView['pendingQuestion'] => {
	const question = state.session.pendingQuestion;

	return question === undefined
		? undefined
		: { kind: question.kind, questionText: question.questionText, suggestions: buildSuggestions(question, bundle) };
};

/** The clock for this turn, without the event id the shell has no use for. */
const pacingOf = (bundle: LoadedGameBundle, state: RuntimeState): SessionView['pacing'] => {
	const pacing = resolvePacing(bundle, state);

	if (pacing === undefined) {
		return undefined;
	}

	return {
		autoWaitMs: pacing.autoWaitMs,
		...(pacing.countdown === undefined
			? {}
			: { countdown: { label: pacing.countdown.label, turnsRemaining: pacing.countdown.turnsRemaining }}),
	};
};

/**
 * Project one runtime state into the view a shell renders.
 *
 * @param bundle - The loaded bundle (the world, and the asset base)
 * @param state - The committed runtime state
 * @returns The view — validated against the contract, so a leak is a thrown error, never a rendered one
 */
export const deriveSessionView = (bundle: LoadedGameBundle, state: RuntimeState): SessionView =>
	sessionViewSchema.parse({
		activeRoomKey: state.progression.activeRoomId,
		ambience: ambienceOf(bundle, state),
		commandHistory: state.session.commandHistory,
		isEnded: terminalEndingOf(bundle, state) !== undefined,
		journal: deriveJournal(bundle, state).map((entry) => ({
			/* v8 ignore next -- a document whose carrier has no home room: the field
			   is optional because `homeRoomNameOf` answers nothing for an item that
			   starts in a container or in hand, and both reference packs place both
			   of their documents in rooms. The absence is carried rather than
			   flattened to an empty string, because "found in ''" would be a
			   sentence the shelf must never print. */
			...(entry.foundInRoomName === undefined ? {} : { foundInRoomName: entry.foundInRoomName }),
			lines: entry.lines.map((line) => ({ priority: line.priority, source: line.source, text: line.text })),
			logId: entry.logId,
			title: entry.title,
		})),
		map: mapOf(bundle, state),
		pacing: pacingOf(bundle, state),
		pendingQuestion: pendingQuestionOf(bundle, state),
		scene: sceneOf(bundle, state),
		// D192: the pack's voices, in declared order; the index is per speaker.
		// D232: an actor's voice carries its line label — the name its lines wear as real text.
		speakers: resolveSpeakers(bundle.contentPack),
		transcript: state.session.transcript,
		turnCount: state.progression.turnCount,
		voices: bundle.contentPack.speakers.map((speaker) => ({
			entries: deriveVoiceIndex(bundle, state, speaker.speakerId),
			name: speaker.name,
			speakerId: speaker.speakerId,
		})),
	} satisfies SessionView);

/**
 * Genesis on a seed, honoring the manifest's seed policy: a `fixed` pack
 * bakes its own seed into every session and refuses a caller's (the engine
 * throws on the contradiction), so the seed is offered only where the
 * manifest says the caller chooses.
 *
 * @param bundle - The loaded bundle
 * @param seed - The caller's seed — used under `caller_supplied`, ignored under `fixed`
 * @param record - D241: the Record the host holds for this game — none when absent
 * @returns The genesis state
 */
export const genesisOf = (bundle: LoadedGameBundle, seed: number, record?: readonly RecordLayer[]): RuntimeState => genesisStateOf(bundle, seed, record);

/** The seed a session actually runs on — the state's, which under `fixed` is the pack's and not the caller's. */
export const seedOf = (state: RuntimeState): number => state.randomness.sessionSeed;

/**
 * D264: the sounds a run of lines makes, in order, resolved to the files a shell fetches — as a field to spread into a
 * turn's outcome, so a turn that made none carries none. D265: each names the entry that says it, when the line is one.
 *
 * @param bundle - The loaded bundle (the table, and the asset base)
 * @param lines - What the turn said: its stamped entries, or the wall's resolved lines
 * @returns `{ cues }`, or nothing
 */
export const cuesOf = (bundle: LoadedGameBundle, lines: readonly { readonly id?: string; readonly cues?: readonly AudioId[]; }[]): Pick<TurnOutcome, 'cues'> => {
	const cues = lines.flatMap((line) =>
		(line.cues ?? []).map((audioId) => {
			const cue = requireCue(bundle, audioId);

			return { ...(line.id === undefined ? {} : { entryId: line.id }), key: cue.audioId, layer: cue.layer, url: resolvePackAssetUrl(bundle.runtimeManifest, cue.src) };
		}),
	);
	const [first, ...rest] = cues;

	return first === undefined ? {} : { cues: [first, ...rest]};
};

/** The committed state every arm of the engine's verdict carries (D23). */
export const nextStateOf = (outcome: PlayerInputOutcome): RuntimeState =>
	outcome.type === 'executed' ? outcome.result.nextState : outcome.nextState;

/**
 * Project one turn's verdict into the shell's turn outcome.
 *
 * @param bundle - The loaded bundle
 * @param outcome - The engine's verdict on the input
 * @param committed - The state the host committed; the verdict's own next state unless the host had a say (D179)
 * @returns The view after the turn, with the verdict's name and the corrections the shell shows
 */
export const deriveTurnOutcome = (bundle: LoadedGameBundle, outcome: PlayerInputOutcome, committed: RuntimeState = nextStateOf(outcome)): TurnOutcome => ({
	corrections: outcome.corrections.map((correction) => ({
		correctedText: correction.correctedText,
		originalText: correction.originalText,
	})),
	...cuesOf(bundle, transcriptEntriesOf(outcome)),
	verdict: outcome.type,
	// D179: the state the host COMMITTED — which, after a `manual` LOAD, is the save the slot held rather than the engine's own next state.
	view: deriveSessionView(bundle, committed),
});
